import { getTimeSeriesSummary } from '../getTimeSeriesSummary'
import { SummaryMode } from '../types'
import type { ChartSeries } from '../types'

/** One breakdown bar: a channel's current value split into a solid base (value at
 *  range start) and a change segment stacked on top (|delta|). Label, icon, and
 *  colour are NOT carried here — those resolve from {@link ChartSeriesContext} by
 *  `id`. */
export interface BreakdownRow {
  id: string
  /** Value at the range start (first non-null reading). */
  start: number
  /** Current value (latest non-null reading); the full bar length for a gain. */
  end: number
  /** Net change over the range (end − start). */
  delta: number
  /** Solid base segment = the floor (lower of start/end). */
  base: number
  /** Change segment stacked on top = |delta|; coloured by direction. */
  change: number
  /** True when the channel gained (or was flat); false when it lost. */
  isGain: boolean
  /** Percent change vs start; null when start <= 0 (no positive baseline). */
  pct: number | null
}

/** Narrow an unknown Recharts tooltip payload to a {@link BreakdownRow}. The
 *  `payload[0].payload` Recharts hands the tooltip is untyped (`any`), so the
 *  tooltip guards on the fields it actually reads rather than trusting the shape. */
export function isBreakdownRow(value: unknown): value is BreakdownRow {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'base' in value &&
    'change' in value &&
    typeof value.id === 'string' &&
    typeof value.base === 'number' &&
    typeof value.change === 'number'
  )
}

/**
 * Build one breakdown bar per primary series, sorted by current value descending.
 * Net change is derived from each series' time series (latest − first) via
 * {@link getTimeSeriesSummary} in LATEST mode, so this chart summarises cumulative
 * (level) metrics like Followers. The solid base is the floor (lower of
 * start/end); a change segment of |delta| stacks on top — coloured by direction —
 * so the bar tip reaches the higher value and a loss is shown rather than hidden.
 * A series with no readings is dropped rather than shown as a zero bar; comparison
 * series are ignored.
 */
export function buildBreakdownRows(series: ChartSeries[]): BreakdownRow[] {
  return series
    .filter((s) => s.kind === 'primary')
    .flatMap((s): BreakdownRow[] => {
      const { latest, change } = getTimeSeriesSummary(
        s.data,
        SummaryMode.LATEST,
      )
      if (latest == null) return []
      const end = latest
      const delta = change ?? 0
      const start = end - delta
      return [
        {
          id: s.id,
          start,
          end,
          delta,
          base: Math.min(start, end),
          change: Math.abs(delta),
          isGain: delta >= 0,
          pct: start > 0 ? (delta / start) * 100 : null,
        },
      ]
    })
    .toSorted((a, b) => b.end - a.end)
}
